import { captureException } from "@sentry/browser";

import logger from "~/lib/logger";

const log = logger();

enum RequestMethod {
  Boot = "boot",
  Status = "status",
  OpenPopup = "open-popup",
  UsageTrackingImportHistory = "usage_tracking_import_history",
}

type Request =
  | {
      method: RequestMethod.Boot;
      params: { clientId: string };
    }
  | {
      method: RequestMethod.Status;
      params?: unknown;
    }
  | {
      method: RequestMethod.OpenPopup;
      params?: unknown;
    }
  | {
      method: RequestMethod.UsageTrackingImportHistory;
      params: { fromDate: string };
    };

type ResponseData = Record<string, boolean | number | string | null>;

type ResponseHeaders = {
  target: "crdbrd-page";
  id: string;
};

type Response =
  | (ResponseHeaders & {
      success: true;
      data?: ResponseData;
    })
  | (ResponseHeaders & {
      success: false;
      message: string;
      data?: ResponseData;
    });

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function isResponseData(value: unknown): value is ResponseData {
  return (
    isRecord(value) &&
    Object.values(value).every(
      (item) =>
        item === null ||
        typeof item === "boolean" ||
        typeof item === "number" ||
        typeof item === "string",
    )
  );
}

function isAddressedTo(value: unknown, requestId: string): boolean {
  return (
    isRecord(value) && value.target === "crdbrd-page" && value.id === requestId
  );
}

export function isResponse(value: unknown): value is Response {
  if (!isRecord(value)) {
    return false;
  }
  if (value.target !== "crdbrd-page" || typeof value.id !== "string") {
    return false;
  }
  if (value.data !== undefined && !isResponseData(value.data)) {
    return false;
  }
  if (value.success === true) {
    return true;
  }
  return value.success === false && typeof value.message === "string";
}

export type ExtensionErrorCode =
  | "not-installed"
  | "communication-error"
  | "extension-rejected";

export class ExtensionError extends Error {
  constructor(
    message: string,
    public code: ExtensionErrorCode,
    public details?: unknown,
    public logLevel: "error" | "warning" | "info" = "error",
  ) {
    super(message);
    this.name = "ExtensionError";
    Object.setPrototypeOf(this, ExtensionError.prototype);
  }

  static NotInstalled(): ExtensionError {
    return new ExtensionError(
      "Browser extension is not installed",
      "not-installed",
      "info",
    );
  }

  static CommunicationError(details?: unknown): ExtensionError {
    return new ExtensionError(
      "Failed to communicate with extension",
      "communication-error",
      details,
    );
  }

  static ExtensionRejected(
    message?: string,
    details?: unknown,
  ): ExtensionError {
    return new ExtensionError(
      message || "Extension rejected the request",
      "extension-rejected",
      details,
    );
  }
}

const VERSION_DOC_ATTR = "data-crdbrd-ext-version";

export class BrowserExtension {
  private version: string | null = null;

  async boot(clientId: string): Promise<Response> {
    await this.check();
    return await this.sendRequest({
      method: RequestMethod.Boot,
      params: {
        clientId: clientId,
      },
    });
  }

  async status({
    minVersion,
  }: {
    minVersion: string;
  }): Promise<"success" | "not-authenticated" | "upgrade-required" | "error"> {
    try {
      await this.check();
      const response = await this.sendRequest({
        method: RequestMethod.Status,
      });
      if (response.data && !response.data.isAuthenticated) {
        return "not-authenticated";
      }
      if (this.isVersion(minVersion) && this.checkVersion({ lt: minVersion })) {
        return "upgrade-required";
      }
      return "success";
    } catch (error) {
      if (error instanceof ExtensionError) {
        captureException(error, { level: error.logLevel });
      } else {
        captureException(error);
      }
      return "error";
    }
  }

  async openPopup(): Promise<boolean> {
    try {
      await this.check();
      if (this.checkVersion({ lt: "1.24.0" })) {
        return false;
      }
      const response = await this.sendRequest({
        method: RequestMethod.OpenPopup,
      });
      return response.success;
    } catch (error) {
      if (error instanceof ExtensionError) {
        captureException(error, { level: error.logLevel });
      } else {
        captureException(error);
      }
      return false;
    }
  }

  async usageTrackingImportHistory(fromDate: string): Promise<Response> {
    await this.check();
    return await this.sendRequest({
      method: RequestMethod.UsageTrackingImportHistory,
      params: {
        fromDate: fromDate,
      },
    });
  }

  async check(): Promise<void> {
    if (!(await this.ready())) {
      throw ExtensionError.NotInstalled();
    }
  }

  private checkVersion({
    lt,
  }: {
    lt: `${number}.${number}.${number}`;
  }): boolean {
    const version = this.getVersion();
    if (!version) {
      throw new Error("Missing version number");
    }
    function splitVersion(value: string): [number, number, number] {
      const parts = value.split(".");
      if (parts.length !== 3) {
        throw new Error("Invalid version format");
      }
      // @ts-expect-error ts does not narrow the list based on length
      return parts.map((part) => Number.parseInt(part, 10));
    }

    const ltParts = splitVersion(lt);
    const versionParts = splitVersion(version);
    return (
      versionParts[0] < ltParts[0] ||
      (versionParts[0] === ltParts[0] && versionParts[1] < ltParts[1]) ||
      (versionParts[0] === ltParts[0] &&
        versionParts[1] === ltParts[1] &&
        versionParts[2] < ltParts[2])
    );
  }

  private getVersion(): string | null {
    if (!this.version) {
      this.version = document.documentElement.getAttribute(VERSION_DOC_ATTR);
      if (this.version) {
        log.debug("Browser extension", this.version, "found");
      }
    }
    return this.version;
  }

  private ready(): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      // Check if content script is already ready
      if (this.getVersion()) {
        resolve(true);
        return;
      }

      // Wait up to 3s for content script to announce presence
      const observer = new MutationObserver(() => {
        if (this.getVersion()) {
          observer.disconnect();
          resolve(true);
        }
      });
      observer.observe(document.documentElement, {
        attributes: true,
        attributeFilter: [VERSION_DOC_ATTR],
      });
      setTimeout(() => {
        observer.disconnect();
        resolve(false);
      }, 1000);
    });
  }

  private sendRequest(request: Request): Promise<Response> {
    const requestId = crypto.randomUUID();

    const responsePromise = new Promise<Response>((resolve, reject) => {
      const responseHandler = (event: MessageEvent<unknown>) => {
        if (event.source !== window || !isAddressedTo(event.data, requestId)) {
          return;
        }

        // Stop listening for responses
        window.removeEventListener("message", responseHandler);

        if (!isResponse(event.data)) {
          log.debug("Discarded malformed response", event.data);
          reject(ExtensionError.CommunicationError("Malformed response"));
          return;
        }

        const response = event.data;
        log.debug("Received response", response);

        if (response.success) {
          resolve(response);
        } else {
          reject(
            ExtensionError.ExtensionRejected(response.message, response.data),
          );
        }
      };

      // Listen for responses
      window.addEventListener("message", responseHandler);
    });

    const timeoutPromise = new Promise<Response>((_, reject) => {
      setTimeout(() => {
        reject(ExtensionError.CommunicationError("Response timeout"));
      }, 10000); // 10 second timeout
    });

    // Send message to extension
    const message = {
      target: "crdbrd-ext",
      id: requestId,
      ...request,
    };
    log.debug("Sending request", message);
    window.postMessage(message, window.location.origin);

    return Promise.race([responsePromise, timeoutPromise]);
  }
  private isVersion(value: string): value is `${number}.${number}.${number}` {
    return /\d+\.\d+\.\d+/.test(value);
  }
}
